import { useEffect, useState } from 'react';
import type { PawnedItem } from '../domain/contractPawnedItems';
import useRequest from './useRequest';

/**
 * The pawned items of one contract, reloaded whenever the contract changes.
 *
 * Lifted out of the Contracts page because the cancellation guard repeats in all three of the
 * request's outcomes, and three `if (!cancelled)` bodies nested inside an effect inside a
 * component is most of what pushed that component past the cognitive-complexity limit. The
 * guard itself is not optional: the selected contract changes faster than the request answers,
 * and a late response would otherwise show the previous contract's items.
 */
export const useContractPawnedItems = (contractNr: number | undefined) => {
    const request = useRequest();
    const [pawnedItems, setPawnedItems] = useState<PawnedItem[]>([]);
    const [pawnedItemsLoading, setPawnedItemsLoading] = useState(false);

    useEffect(() => {
        if (!contractNr) {
            setPawnedItems([]);
            return;
        }

        let cancelled = false;
        const applyIfCurrent = (items: PawnedItem[]) => {
            if (!cancelled) {
                setPawnedItems(items);
            }
        };

        setPawnedItemsLoading(true);
        request<{ contract?: { items?: PawnedItem[] } }>({
            path: `/contracts/${contractNr}`,
            method: 'GET',
        })
            .then(response => applyIfCurrent(response?.contract?.items ?? []))
            .catch(error => {
                console.error('Load pawned items error:', error);
                applyIfCurrent([]);
            })
            .finally(() => {
                if (!cancelled) {
                    setPawnedItemsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [contractNr, request]);

    return { pawnedItems, pawnedItemsLoading };
};

export default useContractPawnedItems;
